/**
 * thread/welcome/index.tsx — Welcome cluster.
 *
 * Contains PdfErrorDialog, ThreadWelcome, ThreadEmptyStateSuggestions,
 * the suggestion catalog, and ThreadSuggestions.
 *
 * Extracted from _legacy.tsx in Phase 4.
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { AssistantIf, useAui, useAuiState } from "@assistant-ui/react";
import {
  ClockIcon,
  FileSearchIcon,
  FolderOpenIcon,
  GitCompareIcon,
  PresentationIcon,
  QuoteIcon,
  ScaleIcon,
  TableIcon,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePdfErrorStore } from "@/lib/stores/pdfErrorStore";
import { apiAuthFetch, selectPraetorCase, selectPraetorDocuments } from "@/lib/authFetch";
import { fetchCaseDocuments } from "@/lib/threadDocumentOps";
import { ClientLogger } from "@/lib/clientLogger";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { useThreadContext, useThreadDocsContext } from "@/components/custom/contexts/threadContext";
import { type RemoteThreadDocument } from "@/components/custom/providers/customThreadListProvider";
import { usePendingNewThreadCaseIdStore } from "@/components/assistant-ui/pendingNewThreadStores";
import { useDocAttachStore, usePendingDocAttachStore } from "@/components/assistant-ui/thread/stores";

// ─── PdfErrorDialog ──────────────────────────────────────────────────────────

export function PdfErrorDialog() {
  const { t } = useTranslation();
  const { open, message, messageParams, onRetry, dismiss } = usePdfErrorStore();
  const handleRetry = () => {
    const cb = onRetry;
    dismiss();
    cb?.();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("thread.cannotOpenFile")}</DialogTitle>
          <DialogDescription>{t(message, messageParams)}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={dismiss}>{t("common.close")}</Button>
          {onRetry && (
            <Button onClick={handleRetry}>{t("common.retry")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Welcome components ───────────────────────────────────────────────────────

const WELCOME_VARIANT_COUNT = 5;

function useWelcomeMessage(category: "case" | "docs" | "general") {
  const { t } = useTranslation();
  const [variant, setVariant] = useState(1);

  useEffect(() => {
    setVariant(Math.floor(Math.random() * WELCOME_VARIANT_COUNT) + 1);
  }, []);

  return t(`thread.welcome.${category}.${variant}`);
}

export function ThreadWelcome() {
  const { caseId } = useRouteContext();
  const { selectedDocs } = useThreadDocsContext();

  const hasDocs = selectedDocs && selectedDocs.length > 0;
  const category =
    caseId === undefined
      ? "general" as const
      : hasDocs
        ? "docs" as const
        : "case" as const;

  const welcomeMessage = useWelcomeMessage(category);

  const suggestions =
    hasDocs
      ? DOCS_SUGGESTIONS
      : CASE_SUGGESTIONS;

  return (
    <div className="aui-thread-welcome-root mx-auto w-full max-w-(--thread-max-width)">
      <AssistantIf condition={({ thread }) => thread.isEmpty}>
        <div className="flex items-center justify-center py-4">
          <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in font-body text-lg text-muted-foreground duration-200 text-center">
            {welcomeMessage}
          </h1>
        </div>
      </AssistantIf>
    </div>
  );
}

export function ThreadEmptyStateSuggestions() {
  return (
    <AssistantIf condition={({ thread }) => thread.isEmpty}>
      <div className="aui-thread-empty-suggestions mt-3">
        <ThreadSuggestions suggestions={ALL_SUGGESTIONS} />
      </div>
    </AssistantIf>
  );
}

// ─── Suggestion catalog ───────────────────────────────────────────────────────

type SuggestionIconKey =
  | "case-summary"
  | "scales"
  | "timeline"
  | "citations"
  | "doc-search"
  | "table"
  | "compare"
  | "guide";

type SuggestionAccent =
  | "blue"
  | "emerald"
  | "purple"
  | "orange"
  | "indigo"
  | "amber";

type Suggestion = {
  labelKey: string;
  promptKey: string;
  descriptionKey: string;
  iconKey: SuggestionIconKey;
  iconAccent: SuggestionAccent;
  requiresDocs: boolean;
  requiresCase?: boolean;
};

const SUGGESTION_ICON_FACTORY: Record<SuggestionIconKey, LucideIcon> = {
  "case-summary": FolderOpenIcon,
  "scales":       ScaleIcon,
  "timeline":     ClockIcon,
  "citations":    QuoteIcon,
  "doc-search":   FileSearchIcon,
  "table":        TableIcon,
  "compare":      GitCompareIcon,
  "guide":        PresentationIcon,
};

// Opacity tints keep the colored badge readable on both the light surface and
// the oklch dark-mode card background. Bumping the alpha slightly in dark mode
// compensates for the lower-contrast surface.
const ACCENT_CLASSES: Record<SuggestionAccent, { bg: string; fg: string }> = {
  blue:    { bg: "bg-blue-500/10 dark:bg-blue-500/15",       fg: "text-blue-600 dark:text-blue-400" },
  emerald: { bg: "bg-emerald-500/10 dark:bg-emerald-500/15", fg: "text-emerald-600 dark:text-emerald-400" },
  purple:  { bg: "bg-purple-500/10 dark:bg-purple-500/15",   fg: "text-purple-600 dark:text-purple-400" },
  orange:  { bg: "bg-orange-500/10 dark:bg-orange-500/15",   fg: "text-orange-600 dark:text-orange-400" },
  indigo:  { bg: "bg-indigo-500/10 dark:bg-indigo-500/15",   fg: "text-indigo-600 dark:text-indigo-400" },
  amber:   { bg: "bg-amber-500/10 dark:bg-amber-500/15",     fg: "text-amber-600 dark:text-amber-400" },
};

const CASE_SUGGESTIONS: readonly Suggestion[] = [
  {
    labelKey: "suggestions.case.basicAnalysis.label",
    descriptionKey: "suggestions.case.basicAnalysis.description",
    promptKey: "suggestions.case.basicAnalysis.prompt",
    iconKey: "case-summary",
    iconAccent: "orange",
    requiresDocs: false,
  },
  {
    labelKey: "suggestions.case.relatedCaseLaw.label",
    descriptionKey: "suggestions.case.relatedCaseLaw.description",
    promptKey: "suggestions.case.relatedCaseLaw.prompt",
    iconKey: "scales",
    iconAccent: "emerald",
    requiresDocs: false,
  },
  {
    labelKey: "suggestions.case.timeline.label",
    descriptionKey: "suggestions.case.timeline.description",
    promptKey: "suggestions.case.timeline.prompt",
    iconKey: "timeline",
    iconAccent: "blue",
    requiresDocs: false,
  },
  {
    labelKey: "suggestions.case.citations.label",
    descriptionKey: "suggestions.case.citations.description",
    promptKey: "suggestions.case.citations.prompt",
    iconKey: "citations",
    iconAccent: "indigo",
    requiresDocs: false,
  },
] as const;

const DOCS_SUGGESTIONS: readonly Suggestion[] = [
  {
    labelKey: "suggestions.docs.basicAnalysis.label",
    descriptionKey: "suggestions.docs.basicAnalysis.description",
    promptKey: "suggestions.docs.basicAnalysis.prompt",
    iconKey: "doc-search",
    iconAccent: "blue",
    requiresDocs: true,
  },
  {
    labelKey: "suggestions.docs.tableAnalysis.label",
    descriptionKey: "suggestions.docs.tableAnalysis.description",
    promptKey: "suggestions.docs.tableAnalysis.prompt",
    iconKey: "table",
    iconAccent: "emerald",
    requiresDocs: true,
  },
  {
    labelKey: "suggestions.docs.comparison.label",
    descriptionKey: "suggestions.docs.comparison.description",
    promptKey: "suggestions.docs.comparison.prompt",
    iconKey: "compare",
    iconAccent: "purple",
    requiresDocs: true,
  },
] as const;

const GENERAL_SUGGESTIONS: readonly Suggestion[] = [
  {
    labelKey: "suggestions.general.guide.label",
    descriptionKey: "suggestions.general.guide.description",
    promptKey: "suggestions.general.guide.prompt",
    iconKey: "guide",
    iconAccent: "purple",
    requiresDocs: false,
    requiresCase: false,
  },
] as const;

const ALL_SUGGESTIONS: readonly Suggestion[] = [...CASE_SUGGESTIONS, ...DOCS_SUGGESTIONS, ...GENERAL_SUGGESTIONS];

function ThreadSuggestions({ suggestions }: { suggestions: readonly Suggestion[] }) {
  const { t } = useTranslation();
  const aui = useAui();
  const routeContext = useRouteContext();
  const threadState = useThreadContext();
  const threadStateRef = useRef(threadState);
  useEffect(() => { threadStateRef.current = threadState; }, [threadState]);

  const threadId = useAuiState(s => s.threadListItem.remoteId);
  const hasRemoteThread = !!threadId && !threadId.startsWith("__LOCAL");
  const beginDocAttach = useDocAttachStore(s => s.begin);
  const endDocAttach = useDocAttachStore(s => s.end);
  const addPendingDocs = usePendingDocAttachStore(s => s.addPending);

  // labelKey of the firing card while a picker is open / send is pending.
  // Disables ALL cards so the user can't fire a second suggestion mid-flight.
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const handleClick = useCallback(async (s: Suggestion) => {
    if (pendingKey) return;
    setPendingKey(s.labelKey);
    try {
      const prompt = t(s.promptKey);
      const initialCaseId = routeContext.caseId ?? null;
      const selectedDocs = threadStateRef.current.selectedDocs ?? [];

      // Resolved post-picker values; the route + docs commit happens once,
      // atomically, after every picker gate has succeeded. Critically, we do
      // NOT commit caseId mid-pipeline — otherwise dismissing the docs
      // picker after a case was picked would strand the user on a case route
      // they never confirmed. `launchCaseId` is intentionally untouched
      // (session-level case context is owned by the launching URL /
      // sessionStorage, not by in-thread suggestion clicks).
      let finalCaseId: string | null = initialCaseId;
      let docsToAttach: RemoteThreadDocument[] | null = null;

      if (s.requiresDocs) {
        // Doc-requiring card: reuse already-attached docs if present.
        if (initialCaseId && selectedDocs.length > 0) {
          // No picker, no doc fetch — fall through to send.
        } else {
          // Either no case yet OR case but no docs: open selectDocuments.
          // When initialCaseId is null the host runs case-pick → docs-pick as
          // one continuous flow; we never call selectCase separately for doc
          // cards. X anywhere returns empty `documents`, which we treat as a
          // clean cancel (no caseId mutation, no message sent).
          beginDocAttach();
          try {
            const picked = await selectPraetorDocuments(initialCaseId);
            if (!picked.documents.length) return;
            finalCaseId = picked.caseId ?? initialCaseId;
            if (!finalCaseId) return;
            docsToAttach = picked.documents.map(p => ({
              docId: p.id, name: p.name, caseId: picked.caseId,
            }));
          } finally {
            endDocAttach();
          }
        }
      } else if (!initialCaseId && s.requiresCase !== false) {
        // Case-only card on /default: pick a case via selectCase, then
        // auto-attach the case's entire document list as selectedDocs (per
        // Daniel's call: "when you select a case, you should have all its
        // documents as selected documents"). When already on /<caseId> we
        // skip both pickers and send with whatever docs are attached — no
        // selection happened, so the auto-attach rule does not fire.
        const picked = await selectPraetorCase();
        if (!picked) return;
        finalCaseId = picked.id;
        try {
          const allDocs = await fetchCaseDocuments(finalCaseId, []);
          if (allDocs.length > 0) docsToAttach = allDocs;
        } catch (fetchErr) {
          // Don't abort the send if the case-docs fetch fails — the user
          // already committed to a case. Log, surface a toast so the user
          // knows the turn is going out without case documents, and proceed.
          ClientLogger.error("Failed to fetch case docs for auto-attach", {
            error: fetchErr instanceof Error ? fetchErr : undefined,
            component: "ThreadSuggestions",
            metadata: { caseId: finalCaseId },
          });
          toast.error(t("suggestions.caseDocsFetchFailed"));
        }
      }

      // Commit caseId via the synchronous handoff store (read by
      // customThreadListProvider.initialize() on first append). The store
      // bypasses the React commit + passive-effect race that the older
      // `setTimeout(0)` + `routeContextRef` path was vulnerable to.
      if (finalCaseId) {
        usePendingNewThreadCaseIdStore.getState().set(finalCaseId);
        if (finalCaseId !== routeContext.caseId) {
          routeContext.onRouteStateChanged({ caseId: finalCaseId });
        }
      }

      // Attach docs (if any). For a remote thread we POST directly; for a
      // not-yet-remote thread we buffer via pendingDocStore + optimistic
      // state, and ComposerDocumentsStrip's flush effect POSTs once the
      // remoteId lands.
      if (docsToAttach && docsToAttach.length > 0) {
        if (hasRemoteThread) {
          const res = await apiAuthFetch(`api/threads/${threadId}/documents`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              caseId: finalCaseId,
              docs: docsToAttach.map(p => ({ docId: p.docId, name: p.name })),
            }),
          });
          if (!res.ok) throw new Error(i18n.t("error.code.service_unavailable"));
          const payload = await res.json() as { selectedDocs: RemoteThreadDocument[] };
          threadStateRef.current.onThreadStateChanged({ selectedDocs: payload.selectedDocs });
        } else {
          addPendingDocs(docsToAttach);
          threadStateRef.current.onThreadStateChanged({ selectedDocs: docsToAttach });
        }
      }

      // Send the prompt. Imperative append mirrors useSuggestionTrigger's
      // internals; AssistantIf unmounts the grid as soon as the thread becomes
      // non-empty, so we don't need to clear pendingKey on success.
      aui.thread().append({
        role: "user",
        content: [{ type: "text", text: prompt }],
        runConfig: aui.composer().getState().runConfig,
      });
    } catch (err) {
      ClientLogger.error("Suggestion click failed", {
        error: err instanceof Error ? err : undefined,
        component: "ThreadSuggestions",
        metadata: { suggestion: s.labelKey },
      });
    } finally {
      setPendingKey(null);
    }
  }, [pendingKey, t, routeContext, hasRemoteThread, threadId, addPendingDocs, beginDocAttach, endDocAttach, aui]);

  return (
    <div className={cn(
      "aui-thread-welcome-suggestions grid w-full max-w-[60rem] mx-auto gap-3 px-2 pb-4",
      "grid-cols-1 @md:grid-cols-2 @4xl:grid-cols-4",
    )}>
      {suggestions.map((suggestion, index) => {
        const prompt = t(suggestion.promptKey);
        const label = t(suggestion.labelKey);
        const description = t(suggestion.descriptionKey);
        const Icon = SUGGESTION_ICON_FACTORY[suggestion.iconKey];
        const accent = ACCENT_CLASSES[suggestion.iconAccent];
        const isPending = pendingKey === suggestion.labelKey;
        return (
          <Tooltip key={suggestion.promptKey}>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={pendingKey !== null}
                onClick={() => handleClick(suggestion)}
                aria-label={label}
                aria-busy={isPending || undefined}
                style={{ animationDelay: `${100 + index * 50}ms` }}
                className={cn(
                  "aui-thread-welcome-suggestion group flex h-full flex-col items-start gap-2 rounded-xl border bg-background px-4 py-3 text-left",
                  "transition-all hover:border-foreground/20 hover:shadow-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "active:scale-[0.99]",
                  "disabled:opacity-50 disabled:pointer-events-none",
                  "animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-200",
                )}
              >
                <span className={cn("flex size-9 items-center justify-center rounded-lg", accent.bg)}>
                  <Icon className={cn("size-5", accent.fg)} />
                </span>
                <span className="text-sm font-semibold leading-tight text-foreground line-clamp-2">
                  {label}
                </span>
                <span className="font-body text-xs leading-snug text-muted-foreground line-clamp-2">
                  {description}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="start"
              className="max-w-sm whitespace-pre-wrap text-xs leading-relaxed"
            >
              {prompt}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
